import { createRootRouteWithContext, Outlet, useRouter } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { Sidebar } from '@/components/layout/Sidebar';
import { Header } from '@/components/layout/Header';
import { useAuth } from '@/hooks/useAuth';
import { useSocketEvents } from '@/hooks/useSocketEvents';
import { Button } from '@/components/ui/button';

interface RootCtx {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RootCtx>()({
  component: RootLayout,
});

function RootLayout() {
  const { isAuthenticated, isLoading } = useAuth();
  const router = useRouter();
  const isLoginRoute = router.state.location.pathname === '/login';
  useSocketEvents();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !isLoginRoute) {
      void router.navigate({ to: '/login' });
    }
  }, [isAuthenticated, isLoading, isLoginRoute, router]);

  // Fermer le drawer mobile à chaque navigation
  useEffect(() => {
    const unsub = router.subscribe('onResolved', () => setMobileOpen(false));
    return unsub;
  }, [router]);

  if (isLoginRoute) {
    return <Outlet />;
  }

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        Chargement…
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-background">
      {/* Sidebar desktop : fixed left, 260px */}
      <aside className="hidden w-[260px] shrink-0 md:flex md:flex-col">
        <Sidebar className="h-full w-full border-r border-border bg-card" />
      </aside>

      {/* Sidebar mobile : drawer overlay */}
      {mobileOpen ? (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <aside className="absolute inset-y-0 left-0 w-[260px] bg-card shadow-xl">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute right-3 top-3 rounded p-1 hover:bg-muted"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" />
            </button>
            <Sidebar className="h-full w-full" />
          </aside>
        </div>
      ) : null}

      {/* Main content */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-16 shrink-0 items-center border-b border-border bg-card">
          {/* Burger mobile */}
          <Button
            variant="ghost"
            size="icon"
            className="ml-2 md:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <Header className="flex-1 border-0" />
        </div>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
